import React, {
  useRef,
  useState,
  useEffect,
  ReactNode,
  useCallback,
  useMemo,
} from 'react';
import heatmapjs from 'heatmap.js';
import { debounce } from 'lodash';

import {
  BlurWrapper,
  DeviceTypeWrapper,
  ElementBox,
  HeatmapCanvas,
  HeatmapTooltipClick,
  HeatmapTooltipHover,
  PageScreenShotImg,
  PageScreenShotWrapper,
  CrossHairTop,
  CrossHairBottom,
  CrossHairLeft,
  CrossHairRight,
  HeatmapTooltipThumbnailWrapper,
  HeatmapTooltipSplit,
  HeatmapTooltipDataRow,
  HeatmapTooltipDataTitle,
  HeatmapTooltipDataValue,
  HeatmapTooltipThumbnailImg,
} from './styled';

import { uxAnalyticsState } from '../../../modules/PageAnalytics/UXAnalytics/types';

import errorImg from '../../../assets/images/icon-noimg@3x.png';

interface MouseEventHeatmapState {
  eventCoordinates: {
    x: number;
    y: number;
    value: number;
    radius: number;
  }[];
  convertEventData: { [key: string]: { [key: string]: number } };
}
interface MouseEventHeatmapProps {
  pageEventStaticsData: uxAnalyticsState['mouseStaticsData'];
  pageElementData: uxAnalyticsState['elementData'];
  targetElementID: uxAnalyticsState['targetElementID'];
  pageId: string;
  tooltipInfo: {
    pathId: string;
    title1: number;
    title2: number;
    title3: number;
  };
  isActiveDataType: {
    click: boolean;
    move: boolean;
    mouseflow: boolean;
    scroll: boolean;
  };
  onSetTargetElementId: (targetElementId: string) => void;
  onSetTooltipInfo: (info: uxAnalyticsState['tooltipInfo']) => void;
}

const MouseEventHeatmap = ({
  pageEventStaticsData,
  pageElementData,
  targetElementID,
  pageId,
  tooltipInfo,
  isActiveDataType,
  onSetTargetElementId,
  onSetTooltipInfo,
}: MouseEventHeatmapProps) => {
  // state
  const pageScreenshotImg = useRef<HTMLImageElement>(null);
  const [isImgLoad, setIsImgLoad] = useState(false);
  const [elementBoxData, setElementBoxData] = useState<
    uxAnalyticsState['elementData'] | null
  >(null);
  const [imgSize, setImgSize] = useState({ width: 0, height: 0 });
  const [targetElementInfo, setTargetElementInfo] = useState({
    x: 0,
    y: 0,
    width: 0,
    height: 0,
  });
  const [clickTooltipCoordinate, setClickTooltipCoordinate] = useState({
    isOpenTooltip: false,
    left: 0,
    top: 0,
    tooltipCase: 0,
  });
  const [hoverTooltipCoordinate, setHoverTooltipCoordinate] = useState({
    isOpenTooltip: false,
    left: 0,
    top: 0,
    tooltipCase: 0,
  });
  const [isMouseOverTargetElement, setIsMouseOverTargetElement] = useState<
    boolean | null
  >(null);
  const [tooltipTitleInfo, setTooltipTitleInfo] = useState({
    title1: '클릭 순위',
    title2: '클릭 횟수',
    title3: '클릭 비율',
    title1Type: '위',
    title2Type: '회',
    title3Type: '%',
  });

  const handleImageLoaded = () => {
    setIsImgLoad(true);
  };

  const handleImageLoadStart = () => {
    setIsImgLoad(false);
  };

  const calculateClickCoordinates = (imgWidth: number, imgHeight: number) => {
    const temp: MouseEventHeatmapState['eventCoordinates'] = [];
    let max = 1;
    if (pageEventStaticsData) {
      const xCategory = Object.keys(pageEventStaticsData.data);

      xCategory.forEach((elementX) => {
        if (elementX !== 'null') {
          const a = pageEventStaticsData.data[elementX];
          const yCategory = Object.keys(a);

          yCategory.forEach((elementY) => {
            if (max < a[elementY]) {
              max = a[elementY];
            }
            temp.push({
              x: Math.floor(Number(elementX) * imgWidth),
              y: Math.floor(Number(elementY) * imgHeight),
              value: a[elementY],
              radius: 20,
            });
          });
        }
      });
    }

    return { temp, max };
  };

  const calculateElementCoordinates = (imgWidth: number, imgHeight: number) => {
    const temp: uxAnalyticsState['elementData'] = [];
    if (pageElementData.length > 0) {
      pageElementData.forEach((element) => {
        // if (
        //   element.x !== 0 &&
        //   element.y !== 0 &&
        //   element.width !== 1 &&
        //   element.height !== 1
        // ) {
        const x = element.x * imgWidth;
        const y = element.y * imgHeight;
        const width = element.width * imgWidth;
        const height = element.height * imgHeight;
        const pushdata = {
          x,
          y,
          width,
          height,
          depth: element.depth,
          element_path: element.element_path,
          cnt: element.cnt,
          rate: element.rate,
          rank: element.rank,
          duration: element.duration,
        };

        temp.push(pushdata);
        // }
      });
    }
    if (temp.length > 0) {
      setElementBoxData(temp);
    }
  };

  const makeHeatmap = (node: HTMLDivElement, width: number, height: number) => {
    if (node) {
      const heatmapCanvasEl = document.getElementById('heatmapCanvas');
      const clickCoordinates = calculateClickCoordinates(width, height);
      if (heatmapCanvasEl) {
        const heatmapCanvas = heatmapjs.create({
          container: heatmapCanvasEl,
        });

        if (node && clickCoordinates.temp) {
          heatmapCanvas.setData({
            data: clickCoordinates.temp,
            max: clickCoordinates.max + 5,
            min: 0,
          });
        }
      }
    }
  };

  const heatmapCanvasRef = useCallback(
    (node: HTMLDivElement | null, width: number, height: number) => {
      if (node !== null) {
        makeHeatmap(node, width, height);
      }
    },
    [pageEventStaticsData]
  );

  const GenerateHeatmapCanvas = (
    width: number,
    height: number
  ): ReactNode[] | null => {
    const result: ReactNode[] | null = [];

    result.push(
      <HeatmapCanvas
        key="heatmapCanvas"
        width={imgSize.width}
        height={imgSize.height}
        id="heatmapCanvas"
        ref={(node) => {
          heatmapCanvasRef(node, width, height);
        }}
      />
    );

    return result;
  };

  const onSetTooltipCoordinate = (
    elementX: number,
    elementY: number,
    elementWidth: number,
    elementHeight: number,
    isClick: boolean
  ) => {
    let tooltipCase = 0;
    let left = elementX - 4;
    let top = elementY + elementHeight + 12;
    const tooltipWidth = 211;
    const tooltipHeight = 229;
    if (elementX > imgSize.width / 2) {
      left = elementX - tooltipWidth + elementWidth;
      tooltipCase += 1;
    }
    if (elementY > imgSize.height / 2) {
      top = elementY - tooltipHeight;
      tooltipCase += 10;
    }
    if (isClick) {
      setClickTooltipCoordinate({
        isOpenTooltip: true,
        left,
        top,
        tooltipCase,
      });
    } else {
      setHoverTooltipCoordinate({
        isOpenTooltip: true,
        left,
        top,
        tooltipCase,
      });
    }
  };

  const onMouseLeaveElementBox = () => {
    setIsMouseOverTargetElement(false);
    setHoverTooltipCoordinate({
      isOpenTooltip: false,
      left: 0,
      top: 0,
      tooltipCase: 0,
    });
  };

  const GenerateElementBox = (): ReactNode[] | null => {
    const result: ReactNode[] | null = [];

    if (elementBoxData) {
      elementBoxData.forEach((element) => {
        const node = (
          <ElementBox
            key={element.element_path}
            isTarget={targetElementID === element.element_path}
            isMouseOverTargetElement={isMouseOverTargetElement}
            onMouseEnter={() => {
              if (isActiveDataType.click) {
                onSetTooltipInfo({
                  pathId: element.element_path,
                  title1: element.rank,
                  title2: element.cnt || 0,
                  title3: element.rate,
                });
              } else if (isActiveDataType.move) {
                onSetTooltipInfo({
                  pathId: element.element_path,
                  title1: element.rank,
                  title2: element.duration || 0,
                  title3: element.rate,
                });
              }

              setIsMouseOverTargetElement(true);
              onSetTooltipCoordinate(
                element.x,
                element.y,
                element.width,
                element.height,
                false
              );
            }}
            onMouseLeave={onMouseLeaveElementBox}
            onClick={() => {
              if (isActiveDataType.click) {
                onSetTooltipInfo({
                  pathId: element.element_path,
                  title1: element.rank,
                  title2: element.cnt || 0,
                  title3: element.rate,
                });
              } else if (isActiveDataType.move) {
                onSetTooltipInfo({
                  pathId: element.element_path,
                  title1: element.rank,
                  title2: element.duration || 0,
                  title3: element.rate,
                });
              }
              onSetTargetElementId(element.element_path);
              setTimeout(() => {
                setIsMouseOverTargetElement(false);
              }, 10);
            }}
            id={element.element_path}
            x={element.x}
            y={element.y}
            width={element.width}
            height={element.height}
            depth={element.depth}
          />
        );

        result.push(node);

        if (targetElementID === element.element_path) {
          setTargetElementInfo({
            x: element.x,
            y: element.y,
            width: element.width,
            height: element.height,
          });
        }
      });
    }

    return result;
  };

  const HeatmapCanvasMemo = useMemo(() => {
    if (
      pageEventStaticsData &&
      Object.keys(pageEventStaticsData.data).length > 0
    ) {
      return GenerateHeatmapCanvas(imgSize.width, imgSize.height);
    }
    return false;
  }, [imgSize, pageEventStaticsData]);

  const TargetElementMemo = useMemo(() => {
    if (pageElementData.length > 0) {
      return GenerateElementBox();
    }
    return false;
  }, [imgSize, pageElementData, targetElementID, isMouseOverTargetElement]);

  useEffect(() => {
    window.addEventListener(
      'resize',
      debounce(() => {
        // window.location.reload(true);
      }, 500)
    );
  }, []);

  useEffect(() => {
    if (isImgLoad && pageScreenshotImg.current) {
      const width = pageScreenshotImg.current.offsetWidth;
      const height = pageScreenshotImg.current.offsetHeight;
      setImgSize({ width, height });
      calculateElementCoordinates(width, height);
    }
  }, [isImgLoad, pageScreenshotImg, pageElementData]);

  useEffect(() => {
    if (targetElementID !== '') {
      setIsMouseOverTargetElement(false);
      onSetTooltipCoordinate(
        targetElementInfo.x,
        targetElementInfo.y,
        targetElementInfo.width,
        targetElementInfo.height,
        true
      );
    } else {
      setIsMouseOverTargetElement(true);
      setClickTooltipCoordinate({
        isOpenTooltip: false,
        left: 0,
        top: 0,
        tooltipCase: 0,
      });
    }
  }, [targetElementID]);

  useEffect(() => {
    if (isActiveDataType.click) {
      setTooltipTitleInfo({
        title1: '클릭 순위',
        title2: '클릭 횟수',
        title3: '클릭 비율',
        title1Type: '위',
        title2Type: '회',
        title3Type: '%',
      });
    } else if (isActiveDataType.move) {
      setTooltipTitleInfo({
        title1: '관심 순위',
        title2: '평균 관심 시간',
        title3: '관심 비율',
        title1Type: '위',
        title2Type: '초',
        title3Type: '%',
      });
    }
  }, [isActiveDataType]);

  return (
    <PageScreenShotWrapper>
      <PageScreenShotImg
        src={`https://voda-media.nerdfactory.ai/${localStorage.getItem(
          'voda_tenant'
        )}/auto/${pageId}/${encodeURIComponent('page_screenshot')}`}
        ref={pageScreenshotImg}
        onLoad={handleImageLoaded}
        onLoadStart={handleImageLoadStart}
      />
      <BlurWrapper />

      {isImgLoad &&
      imgSize.width !== 0 &&
      imgSize.height !== 0 &&
      pageEventStaticsData &&
      Object.keys(pageEventStaticsData.data).length > 0
        ? HeatmapCanvasMemo
        : null}

      {isImgLoad &&
      imgSize.width !== 0 &&
      imgSize.height !== 0 &&
      elementBoxData &&
      elementBoxData.length > 0 &&
      pageElementData.length > 0
        ? TargetElementMemo
        : null}
      <CrossHairTop
        {...targetElementInfo}
        isMouseOverTargetElement={isMouseOverTargetElement}
      />
      <CrossHairBottom
        {...targetElementInfo}
        isMouseOverTargetElement={isMouseOverTargetElement}
      />
      <CrossHairLeft
        {...targetElementInfo}
        isMouseOverTargetElement={isMouseOverTargetElement}
      />
      <CrossHairRight
        {...targetElementInfo}
        isMouseOverTargetElement={isMouseOverTargetElement}
      />
      {/* mouse click 시 나오는 툴팁 */}
      <HeatmapTooltipClick
        {...clickTooltipCoordinate}
        isMouseOverTargetElement={isMouseOverTargetElement}
      >
        <HeatmapTooltipThumbnailWrapper>
          <HeatmapTooltipThumbnailImg
            src={`https://voda-media.nerdfactory.ai/${localStorage.getItem(
              'voda_tenant'
            )}/auto/${pageId}/${encodeURIComponent(tooltipInfo.pathId)}`}
            onError={(event) => {
              const target = event.target as HTMLImageElement;
              target.src = errorImg;
            }}
          />
        </HeatmapTooltipThumbnailWrapper>
        <HeatmapTooltipSplit />
        <HeatmapTooltipDataRow>
          <HeatmapTooltipDataTitle>
            {tooltipTitleInfo.title1}
          </HeatmapTooltipDataTitle>
          <HeatmapTooltipDataValue>
            {tooltipInfo.title1} {tooltipTitleInfo.title1Type}
          </HeatmapTooltipDataValue>
        </HeatmapTooltipDataRow>
        <HeatmapTooltipDataRow>
          <HeatmapTooltipDataTitle>
            {tooltipTitleInfo.title2}
          </HeatmapTooltipDataTitle>
          <HeatmapTooltipDataValue>
            {tooltipInfo.title2} {tooltipTitleInfo.title2Type}
          </HeatmapTooltipDataValue>
        </HeatmapTooltipDataRow>
        <HeatmapTooltipDataRow>
          <HeatmapTooltipDataTitle>
            {tooltipTitleInfo.title3}
          </HeatmapTooltipDataTitle>
          <HeatmapTooltipDataValue>
            {tooltipInfo.title3} {tooltipTitleInfo.title3Type}
          </HeatmapTooltipDataValue>
        </HeatmapTooltipDataRow>
      </HeatmapTooltipClick>
      {/* mouse over 시 나오는 툴팁 */}
      <HeatmapTooltipHover {...hoverTooltipCoordinate}>
        <HeatmapTooltipThumbnailWrapper>
          <HeatmapTooltipThumbnailImg
            src={`https://voda-media.nerdfactory.ai/${localStorage.getItem(
              'voda_tenant'
            )}/auto/${pageId}/${encodeURIComponent(tooltipInfo.pathId)}`}
            onError={(event) => {
              const target = event.target as HTMLImageElement;
              target.src = errorImg;
            }}
          />
        </HeatmapTooltipThumbnailWrapper>
        <HeatmapTooltipSplit />
        <HeatmapTooltipDataRow>
          <HeatmapTooltipDataTitle>
            {tooltipTitleInfo.title1}
          </HeatmapTooltipDataTitle>
          <HeatmapTooltipDataValue>
            {tooltipInfo.title1} {tooltipTitleInfo.title1Type}
          </HeatmapTooltipDataValue>
        </HeatmapTooltipDataRow>
        <HeatmapTooltipDataRow>
          <HeatmapTooltipDataTitle>
            {tooltipTitleInfo.title2}
          </HeatmapTooltipDataTitle>
          <HeatmapTooltipDataValue>
            {tooltipInfo.title2} {tooltipTitleInfo.title2Type}
          </HeatmapTooltipDataValue>
        </HeatmapTooltipDataRow>
        <HeatmapTooltipDataRow>
          <HeatmapTooltipDataTitle>
            {tooltipTitleInfo.title3}
          </HeatmapTooltipDataTitle>
          <HeatmapTooltipDataValue>
            {tooltipInfo.title3} {tooltipTitleInfo.title3Type}
          </HeatmapTooltipDataValue>
        </HeatmapTooltipDataRow>
      </HeatmapTooltipHover>
    </PageScreenShotWrapper>
  );
};

export default MouseEventHeatmap;
